import type { GameId } from '@strategy4kids/protocol';
import type { ComponentType } from 'react';
import type { GameStage } from '../edition/types.js';
import type { GameEntry } from './games-catalog.js';
import type { AppQuery } from './query.js';

/**
 * Web-only games (docs/proposals/web-only-games.md): a game that is in a WEB / desktop build only when the operator's switch
 * (`S4K_WEB_ONLY_GAMES`) turns it on, and never in the native wrapper. Each one is a self-contained module under `src/web-only/<id>/`
 * that registers itself here. The module, its strings and its catalog entry are reachable ONLY through the build-time constant for
 * its id (`__WEB_ONLY_<ID>__`, set by the Vite config), so a build without the switch drops all of it, and the guard test reads the
 * built output to prove no id, screen string or glyph of it is left.
 */
export interface WebOnlyGame {
  readonly id: GameId;
  /** The Games page card. Its `taglineKey` may name another i18n namespace, `ns:key`, which the module loads. */
  readonly entry: GameEntry;
  /** Default stage: a new game starts as `beta`. */
  readonly stage: GameStage;
  readonly Screen: ComponentType<{ onExit: () => void }>;
  /** Begin a game from the app query (the same shape every other game's store takes). */
  start(query: AppQuery): void;
  stop(): void;
}

/** Replaced by Vite's `define` with a literal boolean; undefined in a run with no Vite define (then nothing is registered). */
declare const __WEB_ONLY_POKER__: boolean | undefined;
declare const __WEB_ONLY_BLACKJACK__: boolean | undefined;
/**
 * Adult-only games (src/edition/adult-only.ts) use this same registry, because they have the same shape: a self-contained module under
 * `src/adult-only/<id>/`, reachable only through a build-time constant, that is absent from every other build. The constant is true in an
 * ADULT build (web or native) that includes the game and false everywhere else.
 */
declare const __ADULT_ONLY_MAHJONG__: boolean | undefined;

const registry = new Map<GameId, WebOnlyGame>();

export function registerWebOnlyGame(game: WebOnlyGame): void {
  registry.set(game.id, game);
}
export const webOnlyGame = (id: GameId): WebOnlyGame | undefined => registry.get(id);
export const webOnlyEntries = (): GameEntry[] => [...registry.values()].map((g) => g.entry);
export function webOnlyStages(): Partial<Record<GameId, GameStage>> {
  const out: Partial<Record<GameId, GameStage>> = {};
  for (const g of registry.values()) out[g.id] = g.stage;
  return out;
}
/** Tests only. */
export const clearWebOnlyGames = (): void => registry.clear();

/**
 * Load the web-only games this build includes. Each branch is guarded by a build-time constant, so when it is false the dynamic import
 * is removed and the module never becomes a chunk. `main.tsx` awaits this before the first render.
 */
export async function registerWebOnlyGames(): Promise<void> {
  if (typeof __WEB_ONLY_POKER__ !== 'undefined' && __WEB_ONLY_POKER__) {
    const { pokerGame } = await import('../web-only/poker/index.js');
    registerWebOnlyGame(pokerGame);
  }
  if (typeof __WEB_ONLY_BLACKJACK__ !== 'undefined' && __WEB_ONLY_BLACKJACK__) {
    const { blackjackGame } = await import('../web-only/blackjack/index.js');
    registerWebOnlyGame(blackjackGame);
  }
  if (typeof __ADULT_ONLY_MAHJONG__ !== 'undefined' && __ADULT_ONLY_MAHJONG__) {
    const { mahjongGame } = await import('../adult-only/mahjong/index.js');
    registerWebOnlyGame(mahjongGame);
  }
}
